import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * ODE 05.10.: Videos laden erst, wenn sie in die Nähe des Bildschirms kommen.
 * Vorher lud das 2,5-MB-Video der Newsletter-Fläche im Footer auf JEDER Seite sofort,
 * auch auf dem Handy. Ein Video oben im Bild (Hero) ist sofort sichtbar und lädt wie bisher.
 * Mit "Datensparen" im Browser bleibt es beim Standbild.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    // @ts-ignore
    if (navigator.connection && navigator.connection.saveData) return;

    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#observer?.disconnect();
          this.#observer = null;
          this.#load();
        }
      },
      { rootMargin: '300px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #load() {
    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
